// cmds/MedchemCmds.js
/**
 * @fileoverview
 *
 * Medchem design commands. Implementation layer behind Gibbs registered
 * actions in `gibbs/registry/actions/medchem-design.js`.
 *
 * Currently implemented:
 *   - ScanStructuralAlerts: SMARTS-based toxicophore filter using
 *     RDKit WASM + rules from the Gibbs knowledge tree (medchemRules).
 *   - ReplaceWithBioisostere: SMARTS-driven bioisostere swap via
 *     server-side substructure replacement (Phase 5, 2026-05-03).
 *   - MitigateToxicophore: applies the mitigation recipe for a
 *     detected structural alert (Phase 6, 2026-05-03).
 *   - SwapHeteroaromaticRing: phenyl ↔ pyridine etc. via
 *     HETEROAROMATIC_SWAPS table (Phase 7, 2026-05-03).
 *   - FluorinateAtPosition: F / CF3 / OCF3 / CHF2 placement
 *     (Phase 8, 2026-05-03).
 *
 * The four design handlers (ReplaceWithBioisostere, MitigateToxicophore,
 * SwapHeteroaromaticRing, FluorinateAtPosition) route through the shared
 * `compoundTransform.replaceSubstructure` server-side endpoint.
 *
 * ┌─ ENDPOINT-GATING PATTERN (do not "fix") ─────────────────────────────┐
 * │ The server-side endpoint `POST /services/medchem/replace-substructure/`│
 * │ is owned by Dan and is NOT yet deployed on dev (as of 2026-05-11).    │
 * │                                                                      │
 * │ Until it ships, these four handlers return                           │
 * │   { analogs: [], summary: '...service is unavailable...',            │
 * │     error: 'service_unavailable' }                                   │
 * │ on every invocation. This is the **designed degradation**, not a    │
 * │ bug. The handlers ship today (status: 'implemented' in the registry) │
 * │ and the smoke-test gate flips on automatically once Dan's deploy     │
 * │ lands — no client-side code change required.                         │
 * │                                                                      │
 * │ See `labwork/studies/gibbs-medchem-design-handlers/walkthrough.md`   │
 * │ for the comp chem walkthrough and the rationale for shipping the     │
 * │ client side ahead of the endpoint.                                   │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * ScanStructuralAlerts (Phase 4 / pre-existing) runs entirely client-side
 * via RDKit-WASM and is NOT gated on the endpoint above.
 *
 * @typedef {import('BMapsModel').Compound} Compound
 */

import { App } from 'BMapsSrc/BMapsApp';
import { UserCmd } from 'BMapsCmds/UserCmd';
import { ensureArray } from 'BMapsUtil/js_utils';
// The Gibbs import boundary (test/bmaps/gibbsImportBoundary.test.js) admits a seam only through
// dynamic import(): a static one would pull the target, and everything it imports, into this
// module's bundle. The heavy knowledge modules below are already loaded that way; these two
// public-contract modules now are too, so cmds/MedchemCmds.js is a seam on the same terms.
let publicApiPromise = null;
function publicApi() {
    if (!publicApiPromise) {
        publicApiPromise = Promise.all([
            import('../gibbs/workspace/publicRefRegistry'),
            import('../gibbs/workflow/publicReceipts'),
        ]).then(([refs, receipts]) => ({
            defaultPublicRefRegistry: refs.defaultPublicRefRegistry,
            publicError: receipts.publicError,
            receipt: receipts.receipt,
        }));
    }
    return publicApiPromise;
}

/** @type {{
 *   ScanStructuralAlerts: UserCmd,
 *   ReplaceWithBioisostere: UserCmd,
 *   MitigateToxicophore: UserCmd,
 *   SwapHeteroaromaticRing: UserCmd,
 *   FluorinateAtPosition: UserCmd,
 * }} */
export const MedchemCmds = {
    ScanStructuralAlerts: new UserCmd('ScanStructuralAlerts', scanStructuralAlerts),
    ReplaceWithBioisostere: new UserCmd('ReplaceWithBioisostere', replaceWithBioisostere),
    MitigateToxicophore: new UserCmd('MitigateToxicophore', mitigateToxicophore),
    SwapHeteroaromaticRing: new UserCmd('SwapHeteroaromaticRing', swapHeteroaromaticRing),
    FluorinateAtPosition: new UserCmd('FluorinateAtPosition', fluorinateAtPosition),
};

/**
 * Scan compounds for Meanwell toxicophores and return a rich result:
 *   { results: [{compound, alerts}], summary: <markdown>, flaggedCount }
 *
 * Resolution of which compounds to scan, in priority order:
 *   1. Explicit `compoundsArg` (a Compound or Compound[]).
 *   2. Explicitly selected compounds in the workspace tree (multi-select).
 *   3. All loaded compounds — this is the **default for unscoped scan
 *      requests** like "scan for structural alerts" where the user
 *      didn't name a specific compound. Surfaced 2026-05-14 (Bill
 *      Kinney walkthrough Theme H): Jess noted scanStructuralAlerts
 *      was only seeing the focus compound when she expected it to
 *      scan all loaded compounds. The previous default (active only)
 *      via getSelectedOrActiveCompounds was too narrow.
 *   4. Empty result if no compounds loaded.
 *
 * If the LLM-tier resolves `compounds: "active"` for a specific
 * single-compound intent, the dispatcher passes that single Compound
 * via compoundsArg — falls into branch (1), respecting the user's
 * intent. The new default for "no args" is the natural multi-compound
 * scan.
 *
 * @param {Compound | Compound[] | undefined} compoundsArg
 * @returns {Promise<{results: Array, summary: string, flaggedCount: number}>}
 */
async function scanStructuralAlerts(compoundsArg) {
    let compounds;
    if (compoundsArg) {
        compounds = ensureArray(compoundsArg);
    } else if (App.Workspace) {
        const selected = App.Workspace.getSelectedCompounds() || [];
        if (selected.length > 0) {
            compounds = selected;
        } else {
            // No explicit selection — scan all loaded compounds rather
            // than just the focus. Matches user expectation when the
            // request is unscoped ("scan for structural alerts").
            compounds = App.Workspace.getLoadedCompounds() || [];
        }
    } else {
        compounds = [];
    }

    if (compounds.length === 0) {
        return {
            results: [],
            summary: 'No compound selected or active. Load a compound first.',
            flaggedCount: 0,
        };
    }

    const mod = await import('BMapsSrc/gibbs/knowledge/structuralAlertScanner');
    const { scanCompoundsForAlerts, formatAlertSummary } = mod;

    const compList = compounds.map((c) => ({
        smiles: typeof c.getSmiles === 'function' ? c.getSmiles() : c.smiles,
        name: typeof c.getName === 'function' ? c.getName() : c.name,
        compound: c, // retain reference for downstream consumers
    }));

    const results = await scanCompoundsForAlerts(compList);
    const summary = formatAlertSummary(results);
    const flaggedCount = results.filter((r) => r.alerts.length > 0).length;

    return { results, summary, flaggedCount };
}

// ─── Shared compound resolver ─────────────────────────────────────────────

/**
 * Resolve the compound argument to a single Compound object: explicit
 * argument first, then the workspace's selected/active compound. The
 * design handlers all operate on one compound at a time (multi-compound
 * batches go through scan-structural-alerts and the workflow checker).
 *
 * @param {Compound|undefined} compoundArg
 * @returns {Compound|null}
 */
function resolveActiveCompound(compoundArg) {
    if (compoundArg) {
        const arr = ensureArray(compoundArg);
        return arr[0] || null;
    }
    if (!App.Workspace) return null;
    const list = App.Workspace.getSelectedOrActiveCompounds() || [];
    return list[0] || null;
}

/**
 * Build a client-side reactionSmarts for the fluorinate-at-position
 * handler from a (positionPattern, fluorineGroup) pair. Returns null
 * when the combination isn't in the whitelist — caller falls through
 * to the server-fetch path.
 *
 * Whitelist v1 (gibbs-self-hosted-substructure-replace Path A v2,
 * 2026-05-15). Each entry is validated against a representative input
 * compound — see test/gibbs/RDKitExport.runReaction.test.js.
 *
 * @param {string} positionPattern - SMARTS, normalized
 * @param {string} fluorineGroup - 'F' | 'CF3' | 'OCF3' | 'CHF2'
 * @returns {string|null} reactionSmarts or null
 */
function buildFluorineReactionSmarts(positionPattern, fluorineGroup) {
    const pattern = (positionPattern || '').trim();
    const grp = fluorineGroup || 'F';

    // Methyl (any CH3). Replaces one H with F, or the whole methyl
    // with CF3/OCF3, or two H's with CHF2 (resulting in CHF2 attached
    // where the methyl was).
    if (pattern === '[CH3]' || pattern === '[CX4H3]') {
        if (grp === 'F') return '[*:1][CH3:2]>>[*:1][CH2:2]F';
        if (grp === 'CF3') return '[*:1][CH3:2]>>[*:1]C(F)(F)F';
        if (grp === 'OCF3') return '[*:1][CH3:2]>>[*:1]OC(F)(F)F';
        if (grp === 'CHF2') return '[*:1][CH3:2]>>[*:1][CH:2](F)F';
    }

    // Sp3 methylene (any -CH2- in chain or ring). F replacement is
    // well-defined; the CF3/OCF3 cases lose one of the methylene's
    // two neighbors (chemistry-ambiguous), so we exclude them and
    // require SME-curated SMARTS for those.
    if (pattern === '[CH2;X4]' || pattern === '[CX4H2]'
        || pattern === '[CH2X4]') {
        if (grp === 'F') return '[*:1][CH2;X4:2]>>[*:1][CH:2]F';
    }

    // Aromatic CH (any unsubstituted aromatic carbon). Replaces with
    // an aromatic F. Other groups not well-defined as direct
    // ring-substitutions and need SME review.
    if (pattern === '[cH]') {
        if (grp === 'F') return '[*:1][cH:2]>>[*:1][c:2]F';
    }

    return null;
}

/**
 * Build the tagged analog records the four design handlers return.
 * Each entry pairs an analog SMILES with provenance — origin, parent
 * compound id, and the ruleId that justified the transform — so
 * downstream consumers (Compound Table, ResultsPane, telemetry) can
 * trace the analog back to its source recipe.
 *
 * @param {string[]} analogSmiles
 * @param {Compound} parentCompound
 * @param {string} origin - 'bioisostere' | 'toxicophore-mitigation'
 *   | 'ring-swap' | 'fluorination'
 * @param {Object} provenanceExtras - origin-specific fields
 * @returns {Object[]} analog records
 */
function buildAnalogRecords(analogSmiles, parentCompound, origin, provenanceExtras = {}) {
    const parentId = typeof parentCompound?.getName === 'function'
        ? parentCompound.getName()
        : (parentCompound?.name || null);
    return analogSmiles.map((s) => ({
        smiles: s,
        provenance: {
            origin,
            parent: parentId,
            ...provenanceExtras,
        },
    }));
}

// ─── ReplaceWithBioisostere (Phase 5) ─────────────────────────────────────

/**
 * Apply a cataloged bioisostere swap (Meanwell 2011) to the active
 * compound. Looks up the rule by `ruleId` in the merged built-in +
 * user rule store; if `ruleId` is omitted, the caller MUST provide
 * `fromPattern` and `toFragment` directly.
 *
 * @param {Compound|undefined} compoundArg
 * @param {string} fromPattern - SMARTS pattern of the substructure to replace
 * @param {string} toFragment - Replacement fragment (SMILES)
 * @param {{ ruleId?: string, maxAnalogs?: number }} [options]
 * @returns {Promise<{ analogs: Object[], summary: string, error: string|null }>}
 */
async function replaceWithBioisostere(compoundArg, fromPattern, toFragment, options = {}) {
    const { defaultPublicRefRegistry, publicError, receipt } = await publicApi();
    const compoundRef = compoundArg?.kind === 'compound'
        && typeof compoundArg.workspaceBindingId === 'string' ? compoundArg : null;
    let resolvedCompoundArg = compoundArg;
    let resolvedFromInput = fromPattern;
    let resolvedToInput = toFragment;
    let resolvedOptions = options;
    if (compoundRef) {
        if (fromPattern !== null && typeof fromPattern === 'object' && !Array.isArray(fromPattern)) {
            resolvedOptions = fromPattern;
            resolvedFromInput = undefined;
            resolvedToInput = undefined;
        } else if (fromPattern !== undefined || toFragment !== undefined) {
            throw new TypeError('Public bioisostere generation accepts a catalog ruleId only.');
        }
        const unknown = Object.keys(resolvedOptions).filter((key) => (
            !['ruleId', 'maxAnalogs'].includes(key)
        ));
        if (unknown.length > 0) {
            throw new TypeError(`Unsupported public bioisostere option: ${unknown[0]}.`);
        }
        if (typeof resolvedOptions.ruleId !== 'string' || !resolvedOptions.ruleId) {
            throw new TypeError('Public bioisostere generation requires a catalog ruleId.');
        }
        if (resolvedOptions.maxAnalogs !== undefined
                && (!Number.isInteger(resolvedOptions.maxAnalogs)
                    || resolvedOptions.maxAnalogs < 1 || resolvedOptions.maxAnalogs > 50)) {
            throw new TypeError('maxAnalogs must be an integer from 1 to 50.');
        }
        resolvedCompoundArg = defaultPublicRefRegistry.resolve(compoundRef, 'compound');
    }
    const { ruleId, maxAnalogs = 3 } = resolvedOptions;
    const compound = resolveActiveCompound(resolvedCompoundArg);
    if (!compound) {
        if (compoundRef) {
            return receipt({
                target: compoundRef, ruleId, count: 0, generationOnly: true,
            }, [publicError('COMPOUND_UNAVAILABLE', 'The requested compound is unavailable.')]);
        }
        return {
            analogs: [],
            summary: 'No compound selected or active. Load a compound first.',
            error: 'no_compound',
        };
    }

    // If the caller passed a ruleId, prefer the cataloged SMARTS over
    // any caller-supplied pattern (defensive — a rule citation should
    // mean "use the canonical pattern, not whatever the LLM hallucinated").
    let resolvedFrom = resolvedFromInput;
    let resolvedTo = resolvedToInput;
    // gibbs-self-hosted-substructure-replace Path A (2026-05-15):
    // when the rule carries a `reactionSmarts` (atom-mapped reaction),
    // pass it through so compoundTransform runs the replacement client-
    // side instead of waiting on Dan's server endpoint.
    let resolvedReactionSmarts = null;
    let ruleCitation = '';
    if (ruleId) {
        const lookup = await import('BMapsSrc/gibbs/knowledge/medchemRuleLookup');
        const rule = lookup.getBioisostereByRuleId(ruleId);
        if (compoundRef && !rule) {
            return receipt({
                target: compoundRef, ruleId, count: 0, generationOnly: true,
            }, [publicError('RULE_NOT_FOUND', 'The requested catalog rule is unavailable.')]);
        }
        if (rule) {
            if (rule.fromPattern) resolvedFrom = rule.fromPattern;
            if (rule.toFragment) resolvedTo = rule.toFragment;
            if (rule.reactionSmarts) resolvedReactionSmarts = rule.reactionSmarts;
            const refs = Array.isArray(rule.references) ? rule.references : [];
            ruleCitation = refs.length > 0 ? ` (cited: ${refs[0]})` : '';
        }
    }

    if (!resolvedFrom || !resolvedTo) {
        if (compoundRef) {
            return receipt({
                target: compoundRef, ruleId, count: 0, generationOnly: true,
            }, [publicError('INVALID_RULE', 'The requested catalog rule is incomplete.')]);
        }
        return {
            analogs: [],
            summary: 'Bioisostere replacement requires both fromPattern (SMARTS) and toFragment (SMILES).',
            error: 'invalid_input',
        };
    }

    const inputSmiles = typeof compound.getSmiles === 'function'
        ? compound.getSmiles()
        : compound.smiles;

    const transform = await import('BMapsSrc/gibbs/knowledge/compoundTransform');
    const { analogs: smilesList, error } = await transform.replaceSubstructure(
        inputSmiles, resolvedFrom, resolvedTo,
        { maxResults: maxAnalogs, reactionSmarts: resolvedReactionSmarts },
    );

    if (error || smilesList.length === 0) {
        if (compoundRef) {
            return receipt({
                target: compoundRef, ruleId, count: 0, generationOnly: true,
            }, [publicError(
                error === 'service_unavailable' ? 'GENERATOR_UNAVAILABLE' : 'NO_MATCH',
                error === 'service_unavailable'
                    ? 'The browser-local proposal generator is unavailable.'
                    : 'The requested rule did not produce a proposal.',
            )]);
        }
        return {
            analogs: [],
            summary: error === 'service_unavailable'
                ? 'Substructure replacement service is unavailable. The endpoint may not be deployed yet.'
                : `No sites matched the bioisostere pattern${ruleCitation}.`,
            error: error || null,
        };
    }

    const analogs = buildAnalogRecords(smilesList, compound, 'bioisostere', {
        ruleId: ruleId || null,
        fromPattern: resolvedFrom,
        toFragment: resolvedTo,
    });

    const result = {
        analogs,
        summary: `Generated ${analogs.length} bioisostere analog`
            + `${analogs.length === 1 ? '' : 's'}${ruleCitation}.`,
        error: null,
    };
    return compoundRef ? receipt({
        target: compoundRef, ruleId, count: analogs.length, generationOnly: true,
    }) : result;
}

// ─── MitigateToxicophore (Phase 6) ────────────────────────────────────────

/**
 * Apply the mitigation recipe for a detected structural alert. Looks
 * up the mitigation by `alertType` in the merged rule store and
 * applies its `mitigationPattern` SMARTS to the active compound.
 *
 * Designed to compose with `ScanStructuralAlerts` — the typical chat
 * flow is "scan, then mitigate the aniline alert" → ScanStructuralAlerts
 * surfaces alerts, MitigateToxicophore consumes the alert id.
 *
 * @param {Compound|undefined} compoundArg
 * @param {string} alertType - alert id (e.g., 'aniline', 'furan',
 *   '2-aminothiazole')
 * @param {{ mitigationStrategy?: string, maxAnalogs?: number }} [options]
 * @returns {Promise<{ analogs: Object[], summary: string, error: string|null }>}
 */
async function mitigateToxicophore(compoundArg, alertType, options = {}) {
    const { maxAnalogs = 3 } = options;
    const compound = resolveActiveCompound(compoundArg);
    if (!compound) {
        return {
            analogs: [],
            summary: 'No compound selected or active.',
            error: 'no_compound',
        };
    }
    if (!alertType || typeof alertType !== 'string') {
        return {
            analogs: [],
            summary: 'Mitigation needs an alert type — e.g., "aniline" or "furan".',
            error: 'invalid_input',
        };
    }

    const lookup = await import('BMapsSrc/gibbs/knowledge/medchemRuleLookup');
    const rule = lookup.getMitigationForAlert(alertType);
    if (!rule) {
        return {
            analogs: [],
            summary: `No mitigation recipe found for alert "${alertType}".`,
            error: 'no_mitigation_known',
        };
    }
    // The medchemRuleLookup returns `toFragment` as the replacement SMILES
    // (matching the field name in TOXICOPHORE_RULES). Older code paths also
    // referenced `mitigationReplacement`; accept either for back-compat.
    const replacement = rule.toFragment || rule.mitigationReplacement;
    if (!rule.mitigationPattern || !replacement) {
        return {
            analogs: [],
            summary: `Mitigation for "${alertType}" is pending SME review (no SMARTS encoded yet).`,
            error: 'mitigation_pending_sme_review',
        };
    }

    const inputSmiles = typeof compound.getSmiles === 'function'
        ? compound.getSmiles()
        : compound.smiles;

    const transform = await import('BMapsSrc/gibbs/knowledge/compoundTransform');
    const { analogs: smilesList, error } = await transform.replaceSubstructure(
        inputSmiles, rule.mitigationPattern, replacement,
        {
            maxResults: maxAnalogs,
            // gibbs-self-hosted-substructure-replace Path A (2026-05-15):
            // run client-side when the rule carries a `reactionSmarts`.
            reactionSmarts: rule.reactionSmarts || null,
        },
    );

    if (error || smilesList.length === 0) {
        return {
            analogs: [],
            summary: error === 'service_unavailable'
                ? 'Substructure replacement service is unavailable.'
                : `No sites matched the "${alertType}" mitigation pattern.`,
            error: error || null,
        };
    }

    const refs = Array.isArray(rule.references) ? rule.references : [];
    const citation = refs.length > 0 ? ` (cited: ${refs[0]})` : '';
    return {
        analogs: buildAnalogRecords(smilesList, compound, 'toxicophore-mitigation', {
            alertType,
            ruleId: rule.id || alertType,
        }),
        summary: `Generated ${smilesList.length} mitigated analog`
            + `${smilesList.length === 1 ? '' : 's'} for ${alertType}${citation}.`,
        error: null,
    };
}

// ─── SwapHeteroaromaticRing (Phase 7) ─────────────────────────────────────

/**
 * Swap one heteroaromatic ring class for another using the
 * HETEROAROMATIC_SWAPS table in compoundTransform.js. Common pairs:
 * phenyl ↔ pyridine, thiophene ↔ thiazole, furan ↔ oxazole.
 *
 * @param {Compound|undefined} compoundArg
 * @param {string} sourceRing - ring class (e.g., "phenyl")
 * @param {string} targetRing - ring class (e.g., "pyridine")
 * @param {{ maxAnalogs?: number }} [options]
 * @returns {Promise<{ analogs: Object[], summary: string, error: string|null }>}
 */
async function swapHeteroaromaticRing(compoundArg, sourceRing, targetRing, options = {}) {
    const { maxAnalogs = 3 } = options;
    const compound = resolveActiveCompound(compoundArg);
    if (!compound) {
        return {
            analogs: [],
            summary: 'No compound selected or active.',
            error: 'no_compound',
        };
    }

    // Try the user/built-in merged ring-swap store first; fall back to
    // the hard-coded HETEROAROMATIC_SWAPS in compoundTransform.
    const lookup = await import('BMapsSrc/gibbs/knowledge/medchemRuleLookup');
    const transform = await import('BMapsSrc/gibbs/knowledge/compoundTransform');
    const swap = lookup.getRingSwap(sourceRing, targetRing)
        || transform.getBuiltInRingSwap(sourceRing, targetRing);
    if (!swap) {
        return {
            analogs: [],
            summary: `No ring-swap recipe for ${sourceRing} → ${targetRing}.`,
            error: 'no_swap_known',
        };
    }
    if (!swap.fromPattern || !swap.toFragment) {
        return {
            analogs: [],
            summary: `Ring-swap ${sourceRing} → ${targetRing} is pending SMARTS encoding.`,
            error: 'swap_pending_sme_review',
        };
    }

    const inputSmiles = typeof compound.getSmiles === 'function'
        ? compound.getSmiles()
        : compound.smiles;

    const { analogs: smilesList, error } = await transform.replaceSubstructure(
        inputSmiles, swap.fromPattern, swap.toFragment,
        {
            maxResults: maxAnalogs,
            // gibbs-self-hosted-substructure-replace Path A (2026-05-15):
            // pass reactionSmarts when present on the swap recipe.
            reactionSmarts: swap.reactionSmarts || null,
        },
    );

    if (error || smilesList.length === 0) {
        return {
            analogs: [],
            summary: error === 'service_unavailable'
                ? 'Substructure replacement service is unavailable.'
                : `No ${sourceRing} ring matched in the active compound.`,
            error: error || null,
        };
    }

    return {
        analogs: buildAnalogRecords(smilesList, compound, 'ring-swap', {
            sourceRing, targetRing,
        }),
        summary: `Swapped ${sourceRing} → ${targetRing} (${smilesList.length} analog`
            + `${smilesList.length === 1 ? '' : 's'}). Cited: Meanwell 2017 Adv. Het. Chem.`,
        error: null,
    };
}

// ─── FluorinateAtPosition (Phase 8) ───────────────────────────────────────

/**
 * Add fluorine (or CF3 / OCF3 / CHF2) to the active compound at a
 * SMARTS-matched position. Strategies from Gillis & Meanwell 2015.
 *
 * @param {Compound|undefined} compoundArg
 * @param {string} positionPattern - SMARTS pattern matching the target H atom
 * @param {{
 *   fluorineGroup?: 'F'|'CF3'|'OCF3'|'CHF2',
 *   strategy?: string,
 *   maxAnalogs?: number,
 * }} [options]
 * @returns {Promise<{ analogs: Object[], summary: string, error: string|null }>}
 */
async function fluorinateAtPosition(compoundArg, positionPattern, options = {}) {
    const { fluorineGroup = 'F', strategy = null, maxAnalogs = 3 } = options;
    const compound = resolveActiveCompound(compoundArg);
    if (!compound) {
        return {
            analogs: [],
            summary: 'No compound selected or active.',
            error: 'no_compound',
        };
    }
    if (!positionPattern || typeof positionPattern !== 'string') {
        return {
            analogs: [],
            summary: 'Fluorination needs a SMARTS pattern naming the target atom.',
            error: 'invalid_input',
        };
    }

    // Map the fluorine-group label to a SMILES replacement fragment.
    // The pattern matches a hydrogen-bearing atom; the toFragment carries
    // the same heavy-atom anchor with the F group attached. The simplest
    // pattern is "[H]" → fluorineFragment, which the server applies to
    // every implicit/explicit H matching the positionPattern.
    const groupFragments = {
        F: 'F',
        CF3: 'C(F)(F)F',
        OCF3: 'OC(F)(F)F',
        CHF2: 'C(F)F',
    };
    const toFragment = groupFragments[fluorineGroup] || 'F';

    const inputSmiles = typeof compound.getSmiles === 'function'
        ? compound.getSmiles()
        : compound.smiles;

    // Path A v2 (2026-05-15): build a client-side reactionSmarts from
    // a whitelist of common position patterns + fluorineGroup pairs.
    // Falls through to the legacy server-fetch path when the pattern
    // / group combination isn't in the whitelist.
    const reactionSmarts = buildFluorineReactionSmarts(
        positionPattern, fluorineGroup
    );

    const transform = await import('BMapsSrc/gibbs/knowledge/compoundTransform');
    const { analogs: smilesList, error } = await transform.replaceSubstructure(
        inputSmiles, positionPattern, toFragment,
        { maxResults: maxAnalogs, reactionSmarts },
    );

    if (error || smilesList.length === 0) {
        return {
            analogs: [],
            summary: error === 'service_unavailable'
                ? 'Substructure replacement service is unavailable.'
                : 'No site matched the fluorination pattern.',
            error: error || null,
        };
    }

    const strategyNote = strategy ? ` Strategy: ${strategy}.` : '';
    return {
        analogs: buildAnalogRecords(smilesList, compound, 'fluorination', {
            fluorineGroup, strategy, positionPattern,
        }),
        summary: `Added ${fluorineGroup} at ${smilesList.length} site`
            + `${smilesList.length === 1 ? '' : 's'}.${strategyNote}`
            + ' Cited: Gillis & Meanwell 2015 J. Med. Chem.',
        error: null,
    };
}
